import axios from "axios";
import config from "../../../../config";
import PlatformInterface from "../../../../client/PlatformInterface";

class UserEvents {
  private _apiUrl: string;

  constructor() {
    this._apiUrl = config.apiUrl;
  }

  openScan = async ({
    rootGizmoFid,
    rootGizmoDataFid,
    gizmoFid,
  }: IOpenScanParameters) => {
    const url = `${this._apiUrl}/main_api/v1/monitoring/user-events`;
    const user = await PlatformInterface.getUser();
    const credentials = user.credentials;
    const userEvent: any = {
      fid: crypto.randomUUID(),
      category: "idScanner",
      type: "loadBlinkScan",
      userId: user.id,
      username: user.username,
      rootGizmoFid,
      rootGizmoDataFid,
      gizmoFid,
    };
    const clientData = await getClientData();
    if (clientData) {
      userEvent.data = clientData;
    }

    console.log("User event:", userEvent);

    try {
      const response = await axios.post(
        url,
        {
          userEvents: [userEvent],
        },
        {
          headers: {
            ...credentials,
          },
        },
      );

      if (response.status === 200) {
        console.log("User event successfully sent");
      }
    } catch (e) {}
  };

  scanSuccess = async () => {};
}

interface IOpenScanParameters {
  rootGizmoFid: string;
  rootGizmoDataFid: string;
  gizmoFid: string;
}

export interface IClientData {
  /** Raw UA string — the ground truth the parsed fields are derived from. */
  ua: string;
  os?: string;
  osVersion?: string;
  /** Chromium/Android only; undefined everywhere else (see file header). */
  model?: string;
  browser?: string;
  browserVersion?: string;
  mobile: boolean;
  /** navigator.hardwareConcurrency — counts LITTLE cores too on big.LITTLE. */
  cores?: number;
  /** Chromium only, quantized to powers of two (see file header). */
  memoryGb?: number;
  /** SharedArrayBuffer availability — gates real wasm threading. */
  crossOriginIsolated: boolean;
  /** navigator.gpu exists. NOT proof webgpu works — see webgpu-probe. */
  webgpuAvailable: boolean;
  /** Window context only; absent in a worker. */
  screen?: { width: number; height: number; dpr: number };
  language?: string;
  /** IANA zone, for lining events up with the client's wall clock. */
  timezone?: string;
}

interface IUaBrand {
  brand: string;
  version: string;
}

interface IUaDataValues {
  platform?: string;
  platformVersion?: string;
  model?: string;
  uaFullVersion?: string;
  architecture?: string;
}

interface IUaData {
  brands?: IUaBrand[];
  mobile?: boolean;
  platform?: string;
  getHighEntropyValues?: (hints: string[]) => Promise<IUaDataValues>;
}

let pending: Promise<IClientData> | null = null;

export function getClientData(): Promise<IClientData> {
  if (!pending) pending = collectClientData();
  return pending;
}

async function collectClientData(): Promise<IClientData> {
  try {
    return await collectClientDataUnsafe();
  } catch {
    return {
      ua: typeof navigator !== "undefined" ? (navigator.userAgent ?? "") : "",
      mobile: false,
      crossOriginIsolated:
        typeof crossOriginIsolated !== "undefined" && !!crossOriginIsolated,
      webgpuAvailable: typeof navigator !== "undefined" && "gpu" in navigator,
    };
  }
}

async function collectClientDataUnsafe(): Promise<IClientData> {
  const nav = navigator as Navigator & {
    userAgentData?: IUaData;
    deviceMemory?: number;
  };
  const ua = nav.userAgent ?? "";

  const data: IClientData = {
    ua,
    mobile: /Android|iPhone|iPad|iPod|Mobile/i.test(ua),
    crossOriginIsolated:
      typeof crossOriginIsolated !== "undefined" && !!crossOriginIsolated,
    webgpuAvailable: typeof navigator !== "undefined" && "gpu" in navigator,
    ...parseUserAgent(ua),
  };

  if (typeof nav.hardwareConcurrency === "number") {
    data.cores = nav.hardwareConcurrency;
  }
  if (typeof nav.deviceMemory === "number") {
    data.memoryGb = nav.deviceMemory;
  }
  if (nav.language) data.language = nav.language;

  try {
    data.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    /* Intl unavailable — leave unset */
  }

  // Window-only: a worker has no screen.
  if (typeof globalThis !== "undefined" && "screen" in globalThis) {
    const s = (globalThis as unknown as Window).screen;
    if (s) {
      data.screen = {
        width: s.width,
        height: s.height,
        dpr: (globalThis as unknown as Window).devicePixelRatio ?? 1,
      };
    }
  }

  // Client Hints beat UA parsing where they exist: on Chromium the UA string
  // is frozen/reduced, so `platformVersion` and `model` are only obtainable
  // here. Requires a secure context and is async.
  const uaData = nav.userAgentData;
  if (uaData) {
    if (typeof uaData.mobile === "boolean") data.mobile = uaData.mobile;
    if (uaData.platform) data.os = uaData.platform;

    const brand = uaData.brands?.find(
      (b: any) => !/not.*a.*brand/i.test(b.brand) && !/chromium/i.test(b.brand),
    );
    if (brand) {
      data.browser = brand.brand;
      data.browserVersion = brand.version;
    }

    if (uaData.getHighEntropyValues) {
      try {
        const hints = await uaData.getHighEntropyValues([
          "platformVersion",
          "model",
          "uaFullVersion",
        ]);
        if (hints.platformVersion) data.osVersion = hints.platformVersion;
        // Empty string on desktop/Safari — keep it undefined rather than "".
        if (hints.model) data.model = hints.model;
        if (hints.uaFullVersion) data.browserVersion = hints.uaFullVersion;
      } catch {
        /* insecure context or refused — the parsed values already stand in */
      }
    }
  }

  return data;
}

function parseUserAgent(ua: string): Partial<IClientData> {
  const out: Partial<IClientData> = {};

  const os: [RegExp, string][] = [
    [/Windows NT ([\d.]+)/, "Windows"],
    [/Android ([\d.]+)/, "Android"],
    // iPadOS reports as Mac; isIOS() handles that distinction where it matters.
    [/(?:iPhone|iPad|iPod).*?OS ([\d_]+)/, "iOS"],
    [/Mac OS X ([\d_]+)/, "macOS"],
    [/CrOS \w+ ([\d.]+)/, "ChromeOS"],
    [/Linux/, "Linux"],
  ];
  for (const [re, name] of os) {
    const m = ua.match(re);
    if (m) {
      out.os = name;
      if (m[1]) out.osVersion = m[1].replace(/_/g, ".");
      break;
    }
  }

  // Order matters: Edge/Opera/Samsung all carry "Chrome" in their UA, and
  // Chrome carries "Safari" — so the specific brands must be tested first.
  const browsers: [RegExp, string][] = [
    [/Edg(?:e|A|iOS)?\/([\d.]+)/, "Edge"],
    [/OPR\/([\d.]+)/, "Opera"],
    [/SamsungBrowser\/([\d.]+)/, "Samsung Internet"],
    [/Firefox\/([\d.]+)/, "Firefox"],
    [/CriOS\/([\d.]+)/, "Chrome"],
    [/Chrome\/([\d.]+)/, "Chrome"],
    [/Version\/([\d.]+).*Safari/, "Safari"],
  ];
  for (const [re, name] of browsers) {
    const m = ua.match(re);
    if (m) {
      out.browser = name;
      out.browserVersion = m[1];
      break;
    }
  }

  return out;
}

export default UserEvents;
